import { useState } from 'react';
import {
  X,
  FileText,
  Building2,
  Package,
  Clock,
  CheckCircle2,
  Ban,
  Printer,
  Download,
  Send,
  ChefHat,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { Button } from '@/components/ui/button';
import type { PurchaseOrder } from '../../types';
import {
  PO_STATUS_LABELS, PO_STATUS_COLORS 
} from '../../types';
import type {
  TabValue, Tab 
} from './types';
import { DetailsTab } from './DetailsTab';
import { ItemsTab } from './ItemsTab';
import { PrepListTab } from './PrepListTab';
import { TimelineTab } from './TimelineTab';

interface PurchaseOrderDetailModalProps {
  readonly po: PurchaseOrder;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onApprove?: (poId: string) => void;
  readonly onReject?: (poId: string) => void;
  readonly onSendToSupplier?: (poId: string) => void;
}

const TABS: Tab[] = [
  {
    value: 'details',
    label: 'Details',
    icon: <FileText className="h-4 w-4" /> 
  },
  {
    value: 'items',
    label: 'Line Items',
    icon: <Package className="h-4 w-4" /> 
  },
  {
    value: 'prep-list',
    label: 'Prep List',
    icon: <ChefHat className="h-4 w-4" /> 
  },
  {
    value: 'timeline',
    label: 'Timeline',
    icon: <Clock className="h-4 w-4" /> 
  },
];

export function PurchaseOrderDetailModal({
  po,
  isOpen,
  onClose,
  onApprove,
  onReject,
  onSendToSupplier,
}: PurchaseOrderDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('details');

  if (!isOpen) return null;

  const canApprove = po.status === 'pending';
  const canSend = po.status === 'approved';
  const canCancel = ['draft', 'pending', 'approved'].includes(po.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-50 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <ModalHeader po={po} onClose={onClose} />
        <TabNavigation
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && <DetailsTab po={po} />}
          {activeTab === 'items' && <ItemsTab po={po} />}
          {activeTab === 'prep-list' && <PrepListTab po={po} />}
          {activeTab === 'timeline' && <TimelineTab po={po} />}
        </div>
        <ModalFooter
          po={po}
          canApprove={canApprove}
          canSend={canSend}
          canCancel={canCancel}
          onApprove={onApprove}
          onReject={onReject}
          onSendToSupplier={onSendToSupplier}
        />
      </div>
    </div>
  );
}

function ModalHeader({
  po,
  onClose,
}: {
  readonly po: PurchaseOrder;
  readonly onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b px-6 py-4">
      <div className="flex items-center gap-4">
        <div className="rounded-lg bg-primary/10 p-3">
          <FileText className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{po.poNumber}</h2>
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Building2 className="h-4 w-4" />
            {po.supplierName}
          </p>
        </div>
        <span
          className={cn(
            'ml-4 rounded-full px-3 py-1 text-sm font-medium',
            PO_STATUS_COLORS[po.status]
          )}
        >
          {PO_STATUS_LABELS[po.status]}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function TabNavigation({
  tabs,
  activeTab,
  onTabChange,
}: {
  readonly tabs: Tab[];
  readonly activeTab: TabValue;
  readonly onTabChange: (tab: TabValue) => void;
}) {
  return (
    <div className="flex gap-1 border-b px-6">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onTabChange(tab.value)}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === tab.value
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
          )}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface ModalFooterProps {
  readonly po: PurchaseOrder;
  readonly canApprove: boolean;
  readonly canSend: boolean;
  readonly canCancel: boolean;
  readonly onApprove?: (poId: string) => void;
  readonly onReject?: (poId: string) => void;
  readonly onSendToSupplier?: (poId: string) => void;
}

function ModalFooter({
  po,
  canApprove,
  canSend,
  canCancel,
  onApprove,
  onReject,
  onSendToSupplier,
}: ModalFooterProps) {
  return (
    <div className="flex items-center justify-between border-t px-6 py-4">
      <div className="flex gap-2">
        <Button variant="outline" size="sm">
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          Export PDF
        </Button>
      </div>
      <div className="flex gap-2">
        {canCancel && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReject?.(po.poId)}
          >
            <Ban className="mr-2 h-4 w-4" />
            Cancel PO
          </Button>
        )}
        {canApprove && (
          <Button size="sm" onClick={() => onApprove?.(po.poId)}>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Approve
          </Button>
        )}
        {canSend && (
          <Button size="sm" onClick={() => onSendToSupplier?.(po.poId)}>
            <Send className="mr-2 h-4 w-4" />
            Send to Supplier
          </Button>
        )}
      </div>
    </div>
  );
}
